import React from 'react';

export function ICPBuilderStep({ formData, onChange, onToggleArray, errors }) {
  const companySizes = [
    { value: 'na', label: 'N/A - Generic Content', description: 'Not targeting specific company size' },
    { value: 'startup', label: 'Startup', description: '1-50 employees, early stage' },
    { value: 'smb', label: 'Small to Medium Business', description: '50-500 employees' },
    { value: 'enterprise', label: 'Enterprise', description: '500+ employees, established operations' }
  ];

  const revenueRanges = [
    { value: 'na', label: 'N/A - Generic Content', description: 'Not targeting specific transaction volume' },
    { value: 'under-1m', label: 'Under $1M', description: 'Annual transaction volume' },
    { value: '1m-10m', label: '$1M - $10M', description: 'Annual transaction volume' },
    { value: '10m-100m', label: '$10M - $100M', description: 'Annual transaction volume' },
    { value: 'over-100m', label: 'Over $100M', description: 'Annual transaction volume' }
  ];

  const geographicFootprints = [
    { value: 'single-location', label: 'Single Location', description: 'One physical location' },
    { value: 'regional', label: 'Regional', description: 'Multiple locations in region' },
    { value: 'national', label: 'National', description: 'Locations across country' },
    { value: 'international', label: 'International', description: 'Global presence' }
  ];

  const technologyMaturityLevels = [
    { value: 'na', label: 'N/A - Generic Content', description: 'Not targeting specific technology maturity' },
    { value: 'legacy', label: 'Legacy Systems', description: 'Older technology infrastructure' },
    { value: 'modern', label: 'Modern Infrastructure', description: 'Contemporary technology stack' },
    { value: 'digital-first', label: 'Digital-First', description: 'Cloud-native, modern APIs' }
  ];

  const growthStages = [
    { value: 'established', label: 'Established', description: 'Mature business operations' },
    { value: 'expanding', label: 'Expanding', description: 'Growing market presence' },
    { value: 'transforming', label: 'Transforming', description: 'Modernizing operations' }
  ];

  const decisionFactors = [
    'Cost Optimization',
    'Security & Compliance',
    'Scalability',
    'Integration Ease',
    'Reliability',
    'Customer Experience',
    'Time to Market',
    'Technical Support'
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Ideal Customer Profile Builder</h2>
        <p className="text-gray-600 mb-8">Define the characteristics of your target customers to create more personalized and effective marketing content.</p>
      </div>

      {/* Company Size */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-4">
          Company Size
        </label>
        {errors.companySize && (
          <p className="text-red-600 text-sm mb-2">{errors.companySize}</p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {companySizes.map((size) => (
            <div
              key={size.value}
              className={`relative rounded-lg border p-4 cursor-pointer transition-colors ${
                formData.companySize === size.value
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => onChange('companySize', size.value)}
            >
              <div className="flex items-start">
                <input
                  type="radio"
                  name="companySize"
                  value={size.value}
                  checked={formData.companySize === size.value}
                  onChange={() => onChange('companySize', size.value)}
                  className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 mt-1"
                />
                <div className="ml-3">
                  <label className="block text-sm font-medium text-gray-900">
                    {size.label}
                  </label>
                  <p className="text-sm text-gray-500">{size.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Revenue Range */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-4">
          Annual Transaction Volume
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {revenueRanges.map((range) => (
            <div
              key={range.value}
              className={`relative rounded-lg border p-4 cursor-pointer transition-colors ${
                formData.revenueRange === range.value
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => onChange('revenueRange', range.value)}
            >
              <div className="flex items-start">
                <input
                  type="radio"
                  name="revenueRange"
                  value={range.value}
                  checked={formData.revenueRange === range.value}
                  onChange={() => onChange('revenueRange', range.value)}
                  className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 mt-1"
                />
                <div className="ml-3">
                  <label className="block text-sm font-medium text-gray-900">
                    {range.label}
                  </label>
                  <p className="text-sm text-gray-500">{range.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Geographic Footprint */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-4">
          Geographic Footprint
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {geographicFootprints.map((footprint) => (
            <div
              key={footprint.value}
              className={`relative rounded-lg border p-4 cursor-pointer transition-colors ${
                formData.geographicFootprint === footprint.value
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => onChange('geographicFootprint', footprint.value)}
            >
              <div className="flex items-start">
                <input
                  type="radio"
                  name="geographicFootprint"
                  value={footprint.value}
                  checked={formData.geographicFootprint === footprint.value}
                  onChange={() => onChange('geographicFootprint', footprint.value)}
                  className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 mt-1"
                />
                <div className="ml-3">
                  <label className="block text-sm font-medium text-gray-900">
                    {footprint.label}
                  </label>
                  <p className="text-sm text-gray-500">{footprint.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Technology Maturity */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-4">
          Technology Maturity
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {technologyMaturityLevels.map((level) => (
            <div
              key={level.value}
              className={`relative rounded-lg border p-4 cursor-pointer transition-colors ${
                formData.technologyMaturity === level.value
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => onChange('technologyMaturity', level.value)}
            >
              <div className="flex items-start">
                <input
                  type="radio"
                  name="technologyMaturity"
                  value={level.value}
                  checked={formData.technologyMaturity === level.value}
                  onChange={() => onChange('technologyMaturity', level.value)}
                  className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 mt-1"
                />
                <div className="ml-3">
                  <label className="block text-sm font-medium text-gray-900">
                    {level.label}
                  </label>
                  <p className="text-sm text-gray-500">{level.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Growth Stage */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-4">
          Growth Stage
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {growthStages.map((stage) => (
            <div
              key={stage.value}
              className={`relative rounded-lg border p-4 cursor-pointer transition-colors ${
                formData.growthStage === stage.value
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => onChange('growthStage', stage.value)}
            >
              <div className="flex items-start">
                <input
                  type="radio"
                  name="growthStage"
                  value={stage.value}
                  checked={formData.growthStage === stage.value}
                  onChange={() => onChange('growthStage', stage.value)}
                  className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 mt-1"
                />
                <div className="ml-3">
                  <label className="block text-sm font-medium text-gray-900">
                    {stage.label}
                  </label>
                  <p className="text-sm text-gray-500">{stage.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Key Decision Factors */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-4">
          Key Decision Factors
        </label>
        <p className="text-sm text-gray-500 mb-4">Select the primary factors that influence your target customers' purchasing decisions.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {decisionFactors.map((factor) => (
            <label
              key={factor}
              className={`relative rounded-lg border p-3 cursor-pointer transition-colors ${
                formData.keyDecisionFactors.includes(factor)
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.keyDecisionFactors.includes(factor)}
                  onChange={() => onToggleArray('keyDecisionFactors', factor)}
                  className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm font-medium text-gray-900">
                  {factor}
                </span>
              </div>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}